// components/GrantPremiumAccess.js
import React, { useState } from 'react';
import { doc, setDoc, updateDoc, getDoc, query, where, collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';

const GrantPremiumAccess = () => {
  const [userIdentifier, setUserIdentifier] = useState('');
  const [planType, setPlanType] = useState('sevenDays');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const planOptions = [
    { value: 'sevenDays', label: '7 Days Premium', price: 9 },
    { value: 'oneMonth', label: '1 Month Premium', price: 15 },
    { value: 'lifetime', label: 'Lifetime Premium', price: 79 }
  ];

  const calculateExpiryDate = (planType) => {
    const now = new Date();
    let expiryDate;

    switch (planType) {
      case 'sevenDays':
        expiryDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        break;
      case 'oneMonth':
        expiryDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        break;
      case 'threeMonth':
        expiryDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
        break;
      case 'lifetime':
        expiryDate = new Date(now.setFullYear(now.getFullYear() + 100));
        break;
      default:
        expiryDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    }

    return expiryDate;
  };

  const generatePurchaseId = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const isEmail = (str) => {
    return str.includes('@') && str.includes('.');
  };

  const findUserByEmailOrId = async (identifier) => {
    if (isEmail(identifier)) {
      // Search by email
      const usersCollection = collection(db, 'users');
      const q = query(usersCollection, where('email', '==', identifier));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const userDoc = querySnapshot.docs[0];
      return {
        id: userDoc.id,
        data: userDoc.data()
      };
    } else {
      // Search by user ID
      const userRef = doc(db, 'users', identifier);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return null;
      }
      
      return {
        id: identifier,
        data: userDoc.data()
      };
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    
    if (!userIdentifier.trim()) {
      setMessage('Please enter a valid User ID or Email address');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Find user by email or ID
      const userResult = await findUserByEmailOrId(userIdentifier.trim());

      if (!userResult) {
        setMessage('User not found. Please check the User ID or Email address.');
        setMessageType('error');
        setLoading(false);
        return;
      }

      const { id: userId, data: userData } = userResult;
      const now = new Date();
      const expiryDate = calculateExpiryDate(planType);
      const selectedPlan = planOptions.find(plan => plan.value === planType);

      // Update user document with premium access
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        subscriptionStatus: 'active',
        subscriptionStartDate: Timestamp.fromDate(now),
        expiryDate: Timestamp.fromDate(expiryDate),
        planType: planType,
        paymentProcessor: 'admin_grant',
        updatedAt: Timestamp.fromDate(now),
        maxDownloads: 3
      });

      // Create a purchase record
      const purchaseId = generatePurchaseId();
      const purchaseRef = doc(db, 'purchases', purchaseId);
      
      await setDoc(purchaseRef, {
        amount: 0, //selectedPlan.price * 100, // Convert to cents
        billingType: 'payment',
        createdAt: Timestamp.fromDate(now),
        currency: 'usd',
        customerId: null, // Set to null as requested
        email: userData.email || 'unknown@email.com',
        expiryDate: Timestamp.fromDate(expiryDate),
        paymentProcessor: 'admin_grant',
        paymentStatus: 'paid',
        planType: planType,
        projectId: 'cvdesigner',
        sessionId: null, // Set to null as requested
        status: 'active',
        updatedAt: Timestamp.fromDate(now),
        userId: userId
      });

      setMessage(`Successfully granted ${selectedPlan.label} access to user ${userData.email || userId}`);
      setMessageType('success');
      
      // Reset form
      setUserIdentifier('');
      setPlanType('sevenDays');

    } catch (error) {
      console.error('Error granting premium access:', error);
      setMessage(`Error granting access: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grant-premium-panel">
      <h2>Grant Premium Access</h2>
      <p>Grant premium access to any user by entering their User ID or Email address and selecting a plan type.</p>
      
      <form onSubmit={handleGrantAccess} className="grant-premium-form">
        <div className="form-group">
          <label htmlFor="userIdentifier">User ID or Email Address:</label>
          <input
            type="text"
            id="userIdentifier"
            value={userIdentifier}
            onChange={(e) => setUserIdentifier(e.target.value)}
            placeholder="Enter user ID (e.g., a2hr8yGt...) or email (user@example.com)"
            className="form-input"
            disabled={loading}
          />
          <small className="form-hint">
            You can enter either a User ID or an Email address
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="planType">Plan Type:</label>
          <select
            id="planType"
            value={planType}
            onChange={(e) => setPlanType(e.target.value)}
            className="form-select"
            disabled={loading}
          >
            {planOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label} - ${option.price}
              </option>
            ))}
          </select>
        </div>

        <button 
          type="submit" 
          className="grant-access-button"
          disabled={loading}
        >
          {loading ? 'Granting Access...' : 'Grant Premium Access'}
        </button>
      </form>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default GrantPremiumAccess;